import React, { useEffect, useState } from "react";
import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import { AuthProvider } from "./Hooks/AuthContext";
import { NextUIProvider } from "@nextui-org/react";
import { Row, Col } from "antd";
import MainLayout from "./Routes/MainLayout";
// PromoStack wraps the bare banner and owns --wb-offer-h for the whole promo stack.
import PromoStack from "./Components/PromoStack";
import { WinBackOfferProvider } from "./Hooks/WinBackOfferContext";
import { isEmpty } from "lodash";
import { unAuthGet } from "./Services/api.service";
import { Paths } from "./Services/EndPoints";
import { setStoreDetails, setLocation } from "./Redux/Actions";
import StoreNotFound from "./Pages/Store/StoreNotFound";
import { HistoryProvider } from "./Routes/HistoryContext";
import { useQuery } from "./Hooks/useQuery";
import HelmetMetaData from "./HelmetMetaData";
import { captureUtm } from "./Utils/trafficSource";
import { ensureGuestSessionToken } from "./Utils/HelperFunctions";

// Ad attribution: capture source + medium + campaign at MODULE LOAD — before
// <BrowserRouter> mounts and a redirect-alias route (<Navigate ... replace/>, e.g.
// /cloth-craft -> /craft-cluster/1) can strip the utm query. This reads
// window.location.search synchronously, which is the whole reason it runs here at import
// time rather than in a component.
//
// captureUtm is last-non-direct (see resolveUtm): a tagged landing overwrites, a plainly
// direct one never does. It is cookie-backed and safe to run on every page load.
captureUtm();
function App() {
  const [isVisible, setIsVisible] = useState(false);
  const user = JSON.parse(localStorage.getItem("userData"));
  const query = useQuery();
  const dispatch = useDispatch();
  const storeDetails = useSelector((state) => state.storeDetails);
  const location = useSelector((state) => state.location);
  const [storeData, setStoreData] = useState(null);
  const userData = JSON.parse(localStorage.getItem("userData"));
  const token = userData?.token;

  const isLoggedin = !token ? false : true;

  useEffect(() => {
    if (isEmpty(storeDetails)) {
      unAuthGet(Paths.store_details)
        .then((res) => {
          if (!isEmpty(res?.data)) {
            setStoreData(res?.data);
            // This payload now also carries the storefront coupon (couponCode /
            // couponPercent / couponValidTill), which is why CouponBar can read the
            // slice on both the store home and the PDP without a second request.
            dispatch(setStoreDetails(res?.data));
            if (isLoggedin && res?.data?.consumerLanguage) {
              localStorage.setItem("i18nextLng", res?.data?.consumerLanguage);
            }
          } else {
            setIsVisible(true);
          }
        })
        .catch((err) => {
          console.log(err);
          if (
            err.response?.data?.message ===
            "No store is found with this id and user"
          ) {
            setIsVisible(true);
          }
        });
    }
    // Resolve region/currency once (backend geo-IP for guests via cookie, or
    // the logged-in user's account country). Stored app-wide so payload-less
    // surfaces (address step, login dial-code) and outbound params (countryCode/
    // dialCode) have a single source. Failure → stays empty → domestic default.
    if (isEmpty(location)) {
      unAuthGet(Paths.store_location)
        .then((res) => {
          // Logged so devs can confirm the geo-resolved region while testing
          // via VPN; also lands in Redux (state.location) for the rest of the app.
          console.log("[store/open/location]", res?.data);
          if (!isEmpty(res?.data)) dispatch(setLocation(res.data));
        })
        .catch((err) => console.log(err));
    }

    if (user === null || isEmpty(user)) {
      ensureGuestSessionToken();
    } else {
      sessionStorage.removeItem("sessionToken");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeDetails, user]);

  return (
    <>
      <HelmetMetaData
        title={storeData?.storeName}
        description={storeData?.storeDesc}
        favIcon={storeData?.storeUrl}
        storeLink={storeData?.storeLink}
      />
      <AuthProvider>
        <NextUIProvider>
          <HistoryProvider>
            <Row justify="center">
              <Col
                xxl={14}
                lg={20}
                md={22}
                sm={20}
                xs={24}
                xl={18}
                className="bg-[#F9FAFB]"
                /* No paddingTop reservation any more. The banner is `sticky` and therefore in
                   normal flow, so it occupies its own height inside this Col — which is also
                   what constrains it to the page width. Padding here would double-count it.
                   `--wb-offer-h` is still published by the banner: the PDP root and PDPShimmer
                   subtract it from their 100dvh/100vh heights so the two still sum to one
                   viewport. */
              >
                {isVisible &&
                isEmpty(storeDetails) &&
                query.get("ref") !== "WA" ? (
                  <StoreNotFound />
                ) : (
                  /* App-wide win-back offer state. Wraps MainLayout so the banner and every
                     consumer (PDP payment row, pincode check) share one countdown, driven from
                     the server's absolute validTillEpochMs. */
                  <WinBackOfferProvider>
                    <PromoStack />
                    {/* The page's one <main> landmark, round every route. Pages still render
                        their own header/footer inside it (a per-page <main> is a follow-up). */}
                    <main>
                      <MainLayout />
                    </main>
                  </WinBackOfferProvider>
                )}
              </Col>
            </Row>
          </HistoryProvider>
        </NextUIProvider>
      </AuthProvider>
    </>
  );
}

export default App;
